import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { ChatProfile, FlexStack, Icon } from '~/components/atomic';
import { FullScreenLoading } from '~/components/common';
import { checkGroupChatMember } from '~/api';
import { useModal } from '~/hooks';
import {
  CheckGroupChatMemberRequest,
  GroupChatData,
  ModalParams,
} from '~/types';
import { GAEvent } from '~/utils';
import { GA, PAGE_ROUTE } from '~/constants';
import { useUserStore } from '~/store';
import * as S from './GroupChatItem.styled';

export interface GroupChatItemProps {
  id: GroupChatData['id'];
  characters: GroupChatData['characters'];
  chatTitle: GroupChatData['name'];
  description: GroupChatData['description'];
  language: GroupChatData['language'];
  total: GroupChatData['max_participants_num'];
  count: GroupChatData['participants_num'];
  type?: GroupChatData['room_type'];
}

export default function GroupChatItem({
  id,
  characters,
  chatTitle,
  description,
  language,
  total,
  count,
  type,
}: GroupChatItemProps) {
  const navigate = useNavigate();
  const email = useUserStore((state) => state.email);
  const profile: string[] = characters
    .map((el) => el.thumbnail_url)
    .filter((url): url is string => url !== undefined);
  const [isLoading, setIsLoading] = useState(false);

  const { openModal } = useModal();

  /**
   * 1. type이 public일때
   * 인원이 꽉 찼다면 → isMember여부 확인 → Fulled group chat Modal로 1차 접근 막기
   *
   * 2. type이 private일때
   * 바로 그룹챗으로 이동
   */

  const modalData: ModalParams = {
    title: 'Fulled group chat',
    content: 'This Group chat is currently full. Please try again later.',
    type: 'single',
    buttonName: 'OK',
  };

  const navigateToGroupChat = () => {
    // send GA event : CLICK_JOIN_GROUPCHAT
    GAEvent(email, GA.ACTION.CLICK_JOIN_GROUPCHAT, {
      groupchat_id: id,
      groupchat_name: chatTitle,
      tag_line: description,
      character_id: characters.map((el) => el.id).join(', '),
    });
    navigate(`${PAGE_ROUTE.GROUP_CHAT}/${id}`);
  };

  const handleGroupChat = async () => {
    try {
      setIsLoading(true);
      const requestData: CheckGroupChatMemberRequest = {
        chatroom_id: id,
      };
      const { data, status } = await checkGroupChatMember(requestData);
      if (status !== 200) return;
      const { is_member } = data;
      if (is_member || count < total) {
        navigateToGroupChat();
      } else {
        openModal(modalData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <S.Container role='button' tabIndex={0} onClick={handleGroupChat}>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          gap: 16px;

          .profile {
            flex-shrink: 0;
          }
        `}
      >
        <FlexStack
          spacing={16}
          css={css`
            width: 100%;
          `}
        >
          <S.ChatTitle>{chatTitle}</S.ChatTitle>
          <S.ChatDescription className='desc'>{description}</S.ChatDescription>
        </FlexStack>
        <ChatProfile characters={profile} size={64} />
      </div>

      <S.BottomWrapper>
        <div
          css={css`
            display: flex;
            gap: 8px;
          `}
        >
          <S.RoomInformation language={language}>
            <Icon iconName='Language' />
            <span>{language}</span>
          </S.RoomInformation>
          <S.RoomInformation>
            <Icon iconName='User' />
            <span>
              {count}/{Number(total).toLocaleString('ko-KR')}
            </span>
          </S.RoomInformation>
        </div>

        <S.JoinButton>
          {type === 'private' && <Icon iconName='Lock' />}
          Join
        </S.JoinButton>
      </S.BottomWrapper>
      {isLoading && <FullScreenLoading />}
    </S.Container>
  );
}
